import React, { useEffect, useRef } from 'react';
import { Field } from '@globalvisioninc/ui/field';
import { AllDocumentTypes } from 'types';
import { usePDFPage } from 'pdftron';
import { debounce } from 'lodash';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import styles from './PageSelection.module.css';

interface PageSelectionProps {
  documentType: AllDocumentTypes;
}

const PageSelection = (props: PageSelectionProps) => {
  const pdfPage = usePDFPage(props.documentType);

  // Keep a ref to the latest pdfPage to avoid stale closures
  const pdfPageRef = useRef(pdfPage);
  pdfPageRef.current = pdfPage;

  // Create stable debounced function once
  const debouncedJumpToPage = useRef(
    debounce((pageNumber: number) => {
      pdfPageRef.current.jumpToPage(pageNumber);
    }, 100),
  ).current;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedJumpToPage.cancel();
    };
  }, [debouncedJumpToPage]);

  const changePage = (value: string | null) => {
    const newValue = Number(value ?? '');
    pdfPage.setSelectedPage(newValue);
    if (newValue > 0) {
      debouncedJumpToPage(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      debouncedJumpToPage.cancel();
      pdfPage.autoJumpToPage();
    }
    if (event.key === 'Escape') {
      debouncedJumpToPage.cancel();
      pdfPage.cancelPageSelection();
    }
  };

  const handleBlur = (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Steppers live inside the Field chrome; ignore blurs that stay there.
    const fieldGroup = event?.currentTarget.closest('[role="group"]');
    if (event?.relatedTarget && fieldGroup?.contains(event.relatedTarget as Node)) {
      return;
    }
    debouncedJumpToPage.cancel();
    pdfPage.autoJumpToPage();
  };

  const pageValue = pdfPage.getSelectedPageValue();

  return (
    <div className={styles.root}>
      <GVTextField
        id={`${props.documentType}CurrentPage`}
        label={
          props.documentType === 'source'
            ? 'Source current page'
            : props.documentType === 'target'
              ? 'New current page'
              : 'Current page'
        }
        isLabelHidden
        type="number"
        min={1}
        max={pdfPage.totalPages}
        textAlign="center"
        value={pageValue === '' ? '' : String(pageValue)}
        onValueChange={changePage}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      >
        <Field.Steppers />
      </GVTextField>
      <span className={styles.of}>of</span>
      <span id={`${props.documentType}TotalPageCount`} className={styles.total}>
        {pdfPage.totalPages}
      </span>
    </div>
  );
};

export default PageSelection;
